<template>
  <div v-if="!store.accepted" class="idk-disclaimer-overlay">
    <div class="idk-disclaimer-modal">
      <template v-if="!store.declined">
        <h2 class="idk-disclaimer-title">Disclaimer &amp; Confirmation</h2>

        <div class="idk-disclaimer-body">
          <p>
            This Consent governs your access to and use of www.indiekonnect.com
            and any related digital platforms operated by us (collectively, the
            "Website"), whether you are browsing as a Visitor, registering as a
            Customer, or registering as an Independent Brand Affiliate/Direct
            Seller ("Distributor").
          </p>

          <p>
            By accessing, browsing, or using the Website, creating an account,
            or clicking <strong>"I Agree" / "I Accept"</strong>, you confirm
            that:
          </p>

          <ul>
            <li>
              You have read, understood, and freely consented to be bound by
              this Consent, our Privacy Policy, our Cookie Policy, and, wherever
              applicable, our Return &amp; Refund Policy and Distributor
              Agreement (Policies &amp; Procedures and Code of Ethics).
            </li>
            <li>
              You must be at least 18 years of age and legally competent to
              enter into a binding contract under Indian Law to register on the
              Website as a "Customer" or "Distributor".
            </li>
            <li>
              You must provide accurate, current, and complete information
              during registration and keep such information updated.
            </li>
            <li>
              Distributor registration is additionally governed by the
              Distributor Joining Agreement, Code of Ethics, and Policies &amp;
              Procedures published on the Website, which you separately
              acknowledge and accept during onboarding.
            </li>
            <li>
              You have read and understood this Website Consent &amp; Terms of
              Use, the Privacy Policy, and the Cookie Policy of the website. You
              voluntarily consent to the collection, use, and disclosure of your
              personal data as described therein, and you agree to be bound by
              these terms.
            </li>
          </ul>

          <p>
            If you do not agree with any part of this Consent, please
            discontinue use of the website immediately.
          </p>
        </div>

        <div class="idk-disclaimer-actions">
          <button class="idk-btn-decline" @click="store.decline()">
            Decline
          </button>
          <button class="idk-btn-accept" @click="store.accept()">
            I Agree
          </button>
        </div>
      </template>

      <template v-else>
        <h2 class="idk-disclaimer-title">Access Restricted</h2>
        <div class="idk-disclaimer-body">
          <p>
            You have chosen not to accept our Disclaimer &amp; Confirmation.
            Because acceptance is required to browse or use this website, we're
            unable to grant you access at this time.
          </p>
          <p>
            If you change your mind, you can review and accept the Consent
            below.
          </p>
        </div>
        <div class="idk-disclaimer-actions idk-disclaimer-actions-single">
          <button class="idk-btn-accept" @click="store.reset()">
            Review Consent Again
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useDisclaimerStore } from "@/stores/useDisclaimerStore";
const store = useDisclaimerStore();
</script>

<style scoped>
.idk-disclaimer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(10, 34, 64, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.idk-disclaimer-modal {
  background: #ffffff;
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 40px;
  box-sizing: border-box;
  box-shadow: 0 24px 60px rgba(10, 34, 64, 0.25);
  position: relative;
}
.idk-disclaimer-modal::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  border-radius: 20px 20px 0 0;
  background-color: #ffc72c;
}
.idk-disclaimer-title {
  color: #0a2240;
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 16px;
  letter-spacing: -0.3px;
}
.idk-disclaimer-body p {
  color: #333f48;
  font-size: 14px;
  line-height: 1.7;
  margin-bottom: 12px;
}
.idk-disclaimer-body ul {
  margin: 0 0 12px 20px;
  padding: 0;
}
.idk-disclaimer-body li {
  color: #333f48;
  font-size: 14px;
  line-height: 1.7;
  margin-bottom: 10px;
}
.idk-disclaimer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
.idk-disclaimer-actions-single {
  justify-content: center;
}

/* Primary CTA — same pattern as .next-btn / .register-btn / .shop-now-btn:
   gold fill by default, flips to cobalt with white text on hover */
.idk-btn-accept {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 32px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
  background: linear-gradient(135deg, #ffc72c 0%, #f5b000 100%);
  border: 2px solid #ffc72c;
  color: #0a2240;
  box-shadow: 0 8px 20px rgba(255, 199, 44, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.idk-btn-accept:hover {
  background: #003da5;
  border-color: #003da5;
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 61, 165, 0.3);
  transform: translateY(-1px);
}

/* Secondary / decline — same pattern as .back-btn / .dialog-cancel-btn:
   outlined, cobalt border+text on hover */
.idk-btn-decline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 28px;
  background: transparent;
  color: #5a6b82;
  border: 1.5px solid #d1dbe8;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}
.idk-btn-decline:hover {
  border-color: #003da5;
  color: #003da5;
}

/* RESPONSIVE */
@media (max-width: 480px) {
  .idk-disclaimer-modal {
    padding: 24px 20px;
    border-radius: 16px;
  }
  .idk-disclaimer-title {
    font-size: 19px;
  }
  .idk-disclaimer-actions {
    flex-direction: column-reverse;
  }
  .idk-btn-accept,
  .idk-btn-decline {
    width: 100%;
  }
}
</style>
